import {
  AdministrativeLevels,
  ProjectData,
  UnitType,
} from '../../../lib/propertyTypes';
import config from '../../config';
import { pickMediaURLs } from '../utility/pickMediaUrl';

export type TransformedProjectNetDataType = ReturnType<
  typeof transformProjectnetProjectData
>;

export const transformProjectnetProjectData = (data: ProjectData) => {
  const {
    name,
    completion_year,
    location,
    addresses,
    developer,
    unit_types,
    buildings,
    project_land_area,
    maintenance_fee,
    starting_price,
    media,
    total_units,
    created_at,
    updated_at,
    facilities,
    project_status,
    status,
    streetname,
  } = data;

  const firstUnit = unit_types?.[0] as UnitType;
  const administrativeLevels = addresses[0].administrative_levels || {};
  const { level1, level2, level3 } =
    administrativeLevels as AdministrativeLevels;
  const formattedStartingPrice = starting_price?.value
    ? (starting_price?.value / 1000000).toString()
    : '';
  const formatLandArea = (sqwTotal: number) => {
    const rai = Math.floor(sqwTotal / 400);
    const remainingSqw = sqwTotal % 400;

    const ngaan = Math.floor(remainingSqw / 100);
    const sqw = remainingSqw % 100;

    return `${rai}-${ngaan}-${sqw}`;
  };

  return {
    author: {
      description: '',
      id: developer?.id,
      link: '/author/product/',
      name: developer?.name,
      nickname: developer?.name,
      profileImage: {
        height: 96,
        title: 'Avatar image',
        url: '',
        width: 96,
      },
      total_post: 7,
    },
    buildingProfile: [
      {
        label: 'information',
        value: buildings[0]?.name,
      },
      {
        label: 'num_floor',
        value: buildings[0]?.total_floors?.toString(),
      },
      { label: 'num_room', value: '' },
      {
        label: 'land_area',
        value: project_land_area?.value
          ? formatLandArea(project_land_area.value)
          : null,
      },
      {
        label: 'num_unit',
        value: total_units?.toString(),
      },
      {
        label: 'max_unit_floor',
        value: '', // projectnet api doesn't have field for this
      },
      { label: 'house_size', value: '' },
    ],
    startDate: '', // projectnet api doesn't have field for this
    completionDate: completion_year,
    date: created_at,
    developer: {
      name: developer?.name,
      phone: '',
      website: developer?.website_url || '',
      website_label: developer?.website_url || '',
    },
    facilities:
      facilities?.map((facility) => ({
        label: facility?.code,
        value: facility?.name,
      })) || [],
    featuredImage: {
      height: 266,
      title: '',
      url: media?.photos?.[0]?.V800,
      width: 400,
    },
    heroImage: {
      height: 601,
      title: 'Hero Image',
      url: media?.photos?.[0]?.V800,
      width: 1201,
    },
    media: pickMediaURLs(media?.photos || []),
    id: data?.property_id,
    leadCaptureFormEndDate: '',
    leadCaptureFormId: '',
    leadCaptureFormStartDate: '',
    link: `${
      config.baseUrl
    }project/${firstUnit?.property_type?.property_type?.name.toLowerCase()}/${
      data?.property_id
    }`,
    location: {
      zone: [
        {
          label: level3?.name || '',
          value: '',
          link: level3?.name ? `/list/zone/${level3?.name}` : '',
        },
      ],
      district: [
        {
          label: level1?.name,
          value: '',
          link: `/list/district/${level1?.name}`,
        },
        {
          label: level2?.name,
          value: '',
          link: `/list/district/${level2?.name}`,
        },
      ],
      road: streetname && [
        {
          label: streetname || 'N/A',
          value: '',
          link: '',
        },
      ],
      coordinate: {
        latitude: location?.lat || 0,
        longitude: location?.lon || 0,
      },
    },
    name: name,
    modified: updated_at,
    priceTypes: {
      display_price: starting_price?.value,
      level: {
        label: '', // Price Range (e.g 3 - MAIN CLASS) is supposed to be here but  projectnet api doesn't have field for this
        value: '',
      },
      maintenance_fee: maintenance_fee?.value
        ? `${maintenance_fee.value} บาท/ตร.วา/เดือน`
        : '',
      per_square_meter: '',
      signing_fee: ' ',
      starting_price:
        formattedStartingPrice || String(firstUnit?.price.from.value),
    },
    propertyType: [
      firstUnit?.property_type?.property_type?.name ||
        data.property_type_code.toLowerCase(),
    ],
    roomProfile: {
      ceiling_height: '',
      equipment: { label: '', value: '' },
      sink: { label: '', value: '' },
      stove_hood: { label: '', value: '' },
    },
    status: status,
    statusAvailability: { label: '', value: '' },
    statusDevelopment: {
      label: project_status?.name,
      value: project_status?.code,
    },
    title: name,
    title2: name,
    title3: '',
    totalComments: 0,
    units:
      unit_types?.map((unit) => ({
        building_size: unit?.floor_area?.from.value || 'N/A',
        building_type: {
          label: unit?.property_type?.property_type?.name
            ? `${unit.property_type.property_type.name} ${unit.number_of_floors || 0} ชั้น`
            : 'N/A',
        },
        image: {
          url: unit?.media?.floorplans?.[0]?.url ?? null,
          height: 600,
          width: 1100,
        },
        price: {
          min: unit?.price.from.value || 'N/A',
          max: unit?.price.to.value || '',
        },
        specifications: {
          bathrooms: unit.bath || 0,
          bedrooms: unit.bed || 0,
          garage: unit.parking_spaces || 0,
          number_of_floors: unit.number_of_floors || 0,
        },
        unit_name: unit?.name || '',
      })) || [],
    isProjectnetProject: true,
  };
};

export const transformSuggestionProjectData = (projectsData: ProjectData[]) => {
  if (!projectsData?.length) {
    return [];
  }

  return projectsData.map((project: ProjectData) => ({
    id: project.property_id,
    type: 'project',
    title: project.name,
    subtitle: '',
    image: project.media.photos[0]?.V150 || '', // Provide fallback in case of missing image
    slug: `/project/${project.property_id}`,
    propertyType:
      project.unit_types[0]?.property_type.property_type.name || 'N/A',
    projectNetPropertyId: project.property_id,
    __typename: 'Suggestion',
  }));
};
